import { formatCurrency } from '@crm/shared';
import { Badge, Button, Card, CardContent, cn, Spinner } from '@crm/ui';
import { Check } from 'lucide-react';
import { useCan } from '@/modules/auth/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import { type LicensePlan, usePlans, useSetPlan } from '../api';
import { PlanEmpty, planFeatures } from './plan-summary';

function Preco({ plan }: { plan: LicensePlan }) {
  return (
    <p className="flex items-baseline gap-1">
      <span className="text-3xl font-semibold tracking-tight">
        {formatCurrency(plan.monthlyPrice)}
      </span>
      <span className="text-sm text-muted-foreground">por mes</span>
    </p>
  );
}

/** O que o botao do cartao faz depende de onde o assinante ja esta. */
function acao(plan: LicensePlan, atual: LicensePlan | null): string {
  if (!atual) return 'Escolher';
  if (plan.id === atual.id) return 'Plano atual';

  return plan.monthlyPrice > atual.monthlyPrice ? 'Fazer upgrade' : 'Mudar para este';
}

function Plano({
  plan,
  contratado,
  atual,
  salvando,
  onPick,
}: {
  plan: LicensePlan;
  contratado: boolean;
  atual: LicensePlan | null;
  salvando: boolean;
  onPick: () => void;
}) {
  return (
    <li
      className={cn(
        'flex flex-col gap-4 rounded-card border bg-card p-5',
        contratado ? 'border-primary ring-1 ring-primary/30' : 'border-border',
      )}
    >
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-medium">{plan.name}</p>
          {contratado ? (
            <Badge variant="outline" className="border-primary/50 text-primary">
              Plano atual
            </Badge>
          ) : null}
        </div>
        {plan.description ? (
          <p className="text-sm text-muted-foreground">{plan.description}</p>
        ) : null}
      </div>

      <Preco plan={plan} />

      <ul className="space-y-2 border-t border-border pt-4 text-sm">
        {planFeatures(plan).map((item) => (
          <li key={item} className="flex items-start gap-2">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
            <span className="text-muted-foreground">{item}</span>
          </li>
        ))}
      </ul>

      <Button
        type="button"
        className="mt-auto w-full"
        variant={contratado ? 'outline' : 'default'}
        disabled={contratado || salvando}
        onClick={onPick}
      >
        {acao(plan, atual)}
      </Button>
    </li>
  );
}

export function PlanPicker({ atual }: { atual: string | null }) {
  const podeTrocar = useCan('settings', 'WRITE');
  const plans = usePlans();
  const trocar = useSetPlan();

  if (atual && !podeTrocar) return null;

  const disponiveis = plans.data?.data ?? [];
  const contratado = disponiveis.find((plan) => plan.id === atual) ?? null;

  return (
    <div className="space-y-4">
      {atual ? (
        <p className="text-sm font-medium">Trocar de plano</p>
      ) : (
        <Card variant="flush">
          <CardContent className="py-5">
            <PlanEmpty
              description={
                podeTrocar
                  ? 'O sistema esta liberado sem cobranca. Escolha um plano abaixo para contratar.'
                  : 'O sistema esta liberado sem cobranca. Fale com quem administra a empresa para contratar um plano.'
              }
            />
          </CardContent>
        </Card>
      )}

      {!podeTrocar ? null : plans.isLoading ? (
        <div className="flex justify-center py-6">
          <Spinner className="h-6 w-6 text-muted-foreground" />
        </div>
      ) : (
        <ul className="grid gap-4 md:grid-cols-3">
          {disponiveis.map((plan) => (
            <Plano
              key={plan.id}
              plan={plan}
              contratado={plan.id === atual}
              atual={contratado}
              salvando={trocar.isPending}
              onPick={() => trocar.mutate(plan.id)}
            />
          ))}
        </ul>
      )}

      {podeTrocar && atual ? (
        <Button
          type="button"
          variant="ghost"
          disabled={trocar.isPending}
          onClick={() => trocar.mutate(null)}
        >
          {trocar.isPending ? <Spinner /> : null}
          Cancelar o plano
        </Button>
      ) : null}

      {trocar.error ? (
        <p className="text-sm text-destructive">{toErrorMessage(trocar.error)}</p>
      ) : null}
    </div>
  );
}
